import { __read, __spread } from "tslib";
import { useLayoutEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import ResizeObserverPolyfill from 'resize-observer-polyfill';
import { useRcPortalWindowContext } from '../../contexts';
import { getRefElement, logInDev } from '../../utils';
import { useEventCallback } from '../useEventCallback';
import { useResultRef } from '../useResultRef';
/**
 * @param callback ResizeObserverCallback
 * @param externalWindow options that external window
 */
export var getResizeObserver = function (callback, externalWindow) {
    if (externalWindow) {
        if ((externalWindow === null || externalWindow === void 0 ? void 0 : externalWindow['ResizeObserver']) === undefined) {
            if (process.env.NODE_ENV !== 'production') {
                var message = "window.ResizeObserver is not a constructor,\n    should load polyfill script from 'foundation/polyfill/resize-observer-polyfill/ResizeObserver.global.js' or other CDN polyfill on render window.";
                logInDev({
                    component: 'useResizeObserver',
                    level: 'error',
                    message: message,
                });
                throw Error(message);
            }
            throw Error('window.ResizeObserver not defined');
        }
        return new externalWindow['ResizeObserver'](callback);
    }
    return new ResizeObserverPolyfill(callback);
};
/**
 * get resize event with auto disconnect when layout destroy.
 * bring with debounce for event not too many
 * @param ref that ref for element
 * @param callback callback when element resize
 * @param performance options that improve performance
 */
export var useResizeObserver = function (
/** target element ref */
target, 
/** trigger when `ResizeObserver` emit */
callback, 
/** performance options, `mode`, `time`, `ignoreFireWhenObserve` */
performance) {
    if (performance === void 0) { performance = {}; }
    var _a = performance.mode, mode = _a === void 0 ? 'debounce' : _a, _b = performance.time, performanceTime = _b === void 0 ? 200 : _b, ignoreFireWhenObserve = performance.ignoreFireWhenObserve;
    var countRef = useRef(0);
    var cb = useEventCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (ignoreFireWhenObserve) {
            if (countRef.current === 0) {
                countRef.current = 1;
                return;
            }
        }
        return callback.apply(void 0, __spread(args));
    });
    var externalWindow = useRcPortalWindowContext().externalWindow;
    var observerRef = useResultRef(function () {
        var resizeCallback;
        if (mode === 'debounce') {
            resizeCallback = debounce(cb, performanceTime);
        }
        else if (mode === 'throttle') {
            resizeCallback = throttle(cb, performanceTime);
        }
        else {
            resizeCallback = cb;
        }
        return getResizeObserver(resizeCallback, externalWindow);
    });
    useLayoutEffect(function () {
        var elm = getRefElement(target);
        var observer = observerRef.current;
        if (!elm) {
            throw new Error('please check element exist before bind resize observer');
        }
        observer.observe(elm);
        return function () {
            observer.disconnect();
        };
        // TODO: wait https://github.com/facebook/react/pull/20477 eslint support to remove this exhaustive-deps comment with support custom hooks
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target]);
    return observerRef.current;
};
